import { DraftV1Actions } from 'client/actions/actionTypes'
import { AddProductInterface } from 'client/actions/draft'
import { putProductToLine } from 'client/actions/products'
import { DeliverynoteDraftInterface } from 'client/components/DocumentsV1/Deliverynote/DeliverynoteInterface'
import { ReceiptDraftInterface } from 'client/components/DocumentsV1/Receipt/ReceiptInterface'
import { IGraphQl } from 'client/graphQueries/IGraphQl'
import { CustomerInterface } from 'client/Interfaces/CustomerInterface'
import { DocumentTypes } from 'client/Interfaces/Draft/DraftInterface'
import { Locales } from 'client/Interfaces/Draft/FieldsInterface'
import { LineInterface } from 'client/Interfaces/Draft/LineInterface'
import {
  ConfigInterface,
  DiscountInterface,
  DiscountLocation,
  DiscountType,
  DiscountVAT,
  Language,
} from 'client/Interfaces/Draft/v1/DraftInterface'
import { EditedDraftV1Interface } from 'client/Interfaces/Draft/v1/EditedDraftV1Interface'
import { StateInterface } from 'client/Interfaces/StateInterface'
import {
  calculateValuesForDraftV1,
  calculateValuesForLine,
  perLineDiscountSwicthDiscountColumns,
} from 'client/Other/functions/invoiceLineFunctions'
import { Immutable } from 'client/Other/Immutable'
import { ObjectNumber } from 'client/Other/ObjectNumber'
import { Type } from 'client/Other/Type'
import moment from 'moment'
import { createSelector } from 'reselect'

import { ReducerBuilder } from './ReducerBuilder'

type DraftInterface = ReceiptDraftInterface | DeliverynoteDraftInterface

const INITIAL_STATE: EditedDraftV1Interface = null

type PAYLOAD_TYPES =
  | CustomerInterface
  | EditedDraftV1Interface
  | DraftInterface
  | string
  | LineInterface[]
  | AddProductInterface
  | Array<keyof LineInterface>
  | number
  | string[]
  | EditedDraftV1Interface['company']
  | DiscountType
  | boolean
  | DiscountLocation
  | DiscountVAT

const changeDraftCustomer = (state: EditedDraftV1Interface, payload: CustomerInterface): EditedDraftV1Interface => {
  return { ...state, customer: payload || null, dirty: true }
}

const changeDraft = (state: EditedDraftV1Interface, payload: EditedDraftV1Interface): EditedDraftV1Interface => payload

const changeDeliverynoteNumber = (
  state: EditedDraftV1Interface,
  payload: DeliverynoteDraftInterface['deliverynoteNumber'],
): EditedDraftV1Interface => {
  const deliverynoteNumber = (payload || '').replace(/[^0-9]/g, '').trim()
  if (deliverynoteNumber === '') {
    const { deliverynoteNumber: _, ...rest } = state
    return { ...rest }
  }
  return { ...state, deliverynoteNumber, dirty: true }
}

const changeReceiptNumber = (
  state: EditedDraftV1Interface,
  payload: ReceiptDraftInterface['receiptNumber'],
): EditedDraftV1Interface => {
  const receiptNumber = (payload || '').replace(/[^0-9]/g, '').trim()
  if (receiptNumber === '') {
    const { receiptNumber: _, ...rest } = state
    return { ...rest }
  }
  return { ...state, receiptNumber, dirty: true }
}

const changeInvoiceNumber = (
  state: EditedDraftV1Interface,
  payload: EditedDraftV1Interface['invoiceNumber'],
): EditedDraftV1Interface => {
  const invoiceNumber = (payload || '').trim()
  if (invoiceNumber === '') {
    const { invoiceNumber: _, ...rest } = state
    return { ...rest }
  }
  return { ...state, invoiceNumber, dirty: true }
}

const changeDraftTitle = (state: EditedDraftV1Interface, payload: string): EditedDraftV1Interface => {
  const newState = Immutable.copy(state)
  newState.title = payload
  return { ...newState, dirty: true }
}

const addProductToLine = (state: EditedDraftV1Interface, payload: AddProductInterface): EditedDraftV1Interface => {
  const newState: EditedDraftV1Interface = Immutable.copy(state)
  const { index, product } = payload
  if (newState.lines && newState.lines[index]) {
    newState.lines[index] = putProductToLine(newState.lines[index], product)
  }
  return newState
}

const changeDraftColumnsConfig = (
  state: EditedDraftV1Interface,
  payload: Array<keyof LineInterface>,
): EditedDraftV1Interface => {
  const newState = { ...state }
  newState.config.fields.columns.value = payload
  const newLines = newState.lines as LineInterface[]
  const columnsValue = newState.config.fields.columns.value
  const withoutVatPctColumn = columnsValue.indexOf('vatPct') <= -1
  if (withoutVatPctColumn) {
    for (const line of newLines) {
      if (Type.isInvoiceLine(line)) {
        line.vatPct = 0
        line.taxTotal = 0
      }
    }
  }
  changeDraftLines(state, newLines)
  return newState
}

const changeDraftLanguage = (state: EditedDraftV1Interface, payload: Locales): EditedDraftV1Interface => {
  const newState = Immutable.copy(state)
  newState.language = payload.substring(0, 2) as Language
  newState.config.fields.locale.value = payload
  return { ...newState, dirty: true }
}

const changeDraftCurrency = (state: EditedDraftV1Interface, payload: string): EditedDraftV1Interface => {
  const newState = { ...state }
  for (const line of newState.lines) {
    if (Type.isInvoiceLine(line)) {
      line.currency = payload
    }
  }
  newState.summary.foreignCurrency = payload

  return { ...newState, dirty: true }
}

const changeExchangeRate = (state: EditedDraftV1Interface, payload: number): EditedDraftV1Interface => {
  const newState = { ...state }
  newState.summary.exchangeRate = payload
  return { ...newState, dirty: true }
}

const changeDraftInvalidFields = (state: EditedDraftV1Interface, invalidFields: string[]): EditedDraftV1Interface => {
  const { invalidFields: _, ...rest } = state
  if (invalidFields.length > 0) {
    return { ...rest, invalidFields, dirty: true }
  }
  return { ...rest }
}

const changeDraftDescription = (state: EditedDraftV1Interface, payload: string): EditedDraftV1Interface => {
  return { ...state, description: payload, dirty: true }
}

const changeDraftIssueDate = (state: EditedDraftV1Interface, payload: string): EditedDraftV1Interface => {
  return { ...state, issueDate: payload, dirty: true }
}

const calculateDiscountAndSummary = (state: EditedDraftV1Interface): EditedDraftV1Interface => {
  let currentSubtotal = new ObjectNumber()
  let currentSubvat = new ObjectNumber()
  let currentSubnet = new ObjectNumber()
  let fields = state.config.fields

  if (state.config.fields && state.config.fields.discountEnabled && state.discount) {
    fields = {
      ...fields,
      discountType: {
        value: state.discount.type,
      },
      discountLocation: {
        value: state.discount.location,
      },
      discountVAT: {
        value: state.discount.vat,
      },
    }
  }
  for (const line of state.lines) {
    if (Type.isInvoiceLine(line)) {
      calculateValuesForLine(line, fields, DocumentTypes.RECEIPT)
      currentSubtotal.add(line.grossTotal)
      currentSubvat.add(line.taxTotal)
      currentSubnet.add(line.netTotal)
      line.currency = fields.currency.value
    }

    if (Type.isInvoiceSubtotalLine(line)) {
      line.grossTotal = currentSubtotal.toNumber()
      line.taxTotal = currentSubvat.toNumber()
      line.netTotal = currentSubnet.toNumber()

      currentSubvat = new ObjectNumber()
      currentSubtotal = new ObjectNumber()
      currentSubnet = new ObjectNumber()
    }
  }

  return { ...calculateValuesForDraftV1(state), dirty: true }
}

const changeDraftLines = (state: EditedDraftV1Interface, payload: LineInterface[]): EditedDraftV1Interface => {
  const newState = { ...state, lines: payload }
  if (newState.documentType.includes(DocumentTypes.DELIVERYNOTE)) {
    // Summary and Subtotal line calculation not needed
    return { ...newState, dirty: true }
  } else {
    return { ...calculateDiscountAndSummary(newState) }
  }
}

const changeDraftDiscountType = (state: EditedDraftV1Interface, type: DiscountType): EditedDraftV1Interface => {
  return { ...calculateDiscountAndSummary({ ...state, discount: { ...state.discount, type } }) }
}

const changeDraftDiscountVat = (state: EditedDraftV1Interface, vat: DiscountVAT): EditedDraftV1Interface => {
  return { ...calculateDiscountAndSummary({ ...state, discount: { ...state.discount, vat } }) }
}

const changeDraftDiscountLocation = (
  state: EditedDraftV1Interface,
  location: DiscountLocation,
): EditedDraftV1Interface => {
  // change discount location requries to clear discount first
  const newLines = state.lines as LineInterface[]
  newLines.forEach((line) => {
    line.discount = 0
    line.totalDiscount = 0
  })
  changeDraftLines(state, newLines)
  // change discount location requries to change columns
  const columns = [...state.config.fields.columns.value] as Array<keyof LineInterface>
  const newColumns = perLineDiscountSwicthDiscountColumns(true, location, columns)
  changeDraftColumnsConfig(state, newColumns)
  return { ...calculateValuesForDraftV1({ ...state, discount: { ...state.discount, location } }), dirty: true }
}

const changeDraftDiscountAmount = (state: EditedDraftV1Interface, payload: number): EditedDraftV1Interface => {
  return {
    ...calculateValuesForDraftV1({ ...state, discount: { ...state.discount, amount: payload } }),
    dirty: true,
  }
}

const changeDraftDiscountEnabled = (state: EditedDraftV1Interface, enabled: boolean): EditedDraftV1Interface => {
  // changeDraftDiscountEnabled requries to clear discount first
  const newLines = state.lines as LineInterface[]
  newLines.forEach((line) => {
    line.discount = 0
    line.totalDiscount = 0
  })
  changeDraftLines(state, newLines)
  const { discount: _, ...rest } = {
    ...state,
    config: {
      ...state.config,
      fields: {
        ...state.config.fields,
        discountEnabled: { value: enabled, visible: true },
      },
    },
  }
  const discount: DiscountInterface = {
    amount: 0,
    type: DiscountType.Amount,
    vat: DiscountVAT.PreVat,
    location: DiscountLocation.Line,
  }
  const columns = [...state.config.fields.columns.value] as Array<keyof LineInterface>
  const newColumns = perLineDiscountSwicthDiscountColumns(enabled, discount.location, columns)

  if (!enabled) {
    changeDraftColumnsConfig(state, newColumns)
    return { ...rest }
  }
  changeDraftColumnsConfig(state, newColumns)
  return { ...rest, discount, dirty: true }
}

const changeDraftCompany = (
  state: EditedDraftV1Interface,
  payload: EditedDraftV1Interface['company'],
): EditedDraftV1Interface => ({
  ...state,
  company: payload,
  dirty: true,
})

const copyDraft = (state: EditedDraftV1Interface, payload: DraftInterface): EditedDraftV1Interface => {
  return { ...payload, id: null, issueDate: moment().toISOString() }
}

const changeDraftFooterText = (state: EditedDraftV1Interface, payload: string): EditedDraftV1Interface => {
  return { ...state, footerText: payload, dirty: true }
}

const removeTypename = (obj: IGraphQl<ConfigInterface> | ConfigInterface): ConfigInterface => {
  const config = JSON.parse(
    JSON.stringify(obj, (k, v: boolean | string) => {
      return k === '__typename' || k === 'name' ? undefined : v
    }),
  ) as ConfigInterface
  Object.keys(config.fields).forEach(
    (key) => (config.fields[key] === null || key === '__typename') && delete config.fields[key],
  )
  return config
}

const cleanDirtyDraft = (state: EditedDraftV1Interface): Omit<EditedDraftV1Interface, 'dirty'> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { dirty, ...withoutDirty } = { ...state }
  return { ...withoutDirty, config: { ...removeTypename(withoutDirty.config) } }
}

const loadNewDraft = (
  state: EditedDraftV1Interface,
  payload: Omit<EditedDraftV1Interface, 'config'> & { config: IGraphQl<ConfigInterface> },
): EditedDraftV1Interface => {
  return { ...payload, config: { ...removeTypename(payload.config) } }
}

export default new ReducerBuilder<EditedDraftV1Interface, PAYLOAD_TYPES>(INITIAL_STATE)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_COMPANY, changeDraftCompany)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_CUSTOMER, changeDraftCustomer)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_DELIVERYNOTE_NUMBER, changeDeliverynoteNumber)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_RECEIPT_NUMBER, changeReceiptNumber)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_INVOICE_NUMBER, changeInvoiceNumber)
  .mapAction(DraftV1Actions.DRAFT_V1_LOAD_NEW, loadNewDraft)
  .mapAction(DraftV1Actions.DRAFT_V1_LOAD, changeDraft)
  .mapAction(DraftV1Actions.DRAFT_V1_LOAD_APPROVED, changeDraft)
  .mapAction(DraftV1Actions.DRAFT_V1_SAVE_DRAFT, changeDraft)
  .mapAction(DraftV1Actions.DRAFT_V1_UPDATE_DRAFT, changeDraft)
  .mapAction(DraftV1Actions.DRAFT_V1_UPDATE_APPROVED, changeDraft)
  .mapAction(DraftV1Actions.DRAFT_V1_COPY_APPROVED, changeDraft)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_TITLE, changeDraftTitle)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_DESCRIPTION, changeDraftDescription)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_ISSUE_DATE, changeDraftIssueDate)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_FOOTER_TEXT, changeDraftFooterText)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_DISCOUNT_AMOUNT, changeDraftDiscountAmount)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_DISCOUNT_VAT, changeDraftDiscountVat)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_DISCOUNT_TYPE, changeDraftDiscountType)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_DISCOUNT_ENABLED, changeDraftDiscountEnabled)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_DISCOUNT_LOCATION, changeDraftDiscountLocation)
  .mapAction(DraftV1Actions.DRAFT_V1_CLEAR, () => INITIAL_STATE)
  .mapAction(DraftV1Actions.DRAFT_V1_CLEAN_DIRTY, cleanDirtyDraft)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_LINES, changeDraftLines)
  .mapAction(DraftV1Actions.DRAFT_V1_ADD_PRODUCT_LINE, addProductToLine)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_COLUMNS_CONFIG, changeDraftColumnsConfig)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_LANGUAGE, changeDraftLanguage)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_CURRENCY, changeDraftCurrency)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_EXCHANGE_RATE, changeExchangeRate)
  .mapAction(DraftV1Actions.DRAFT_V1_CHANGE_DRAFT_INVALID_FIELDS, changeDraftInvalidFields)
  .mapAction(DraftV1Actions.DRAFT_V1_COPY, copyDraft)
  .build()

// Selectors
export const getDraftV1Selector = (state: StateInterface): EditedDraftV1Interface => state.draftV1

export const titleSelector = createSelector<StateInterface, EditedDraftV1Interface, string>(
  getDraftV1Selector,
  (draft) => (draft && draft.title ? draft.title : ''),
)

export const localeSelector = createSelector<StateInterface, EditedDraftV1Interface, Locales>(
  getDraftV1Selector,
  (draft) => draft.config.fields.locale && draft.config.fields.locale.value,
)
